import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { updateCachedSettings } from "@/hooks/useSiteSettings";
import { toast } from "sonner";
import { Upload, Loader2, X, Plus, Save, Download, FileUp, Send, AlertTriangle, ImagePlus, Trash2, Link as LinkIcon, RefreshCw, Palette, RotateCcw, Check } from "lucide-react";
import { THEMES, THEME_KEYS, DEFAULT_THEME_ID, getThemeById, resolvePalette } from "@/lib/themes";
import { applyTheme } from "@/lib/themeApplier";

export default function AdminSite() {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/site");
      setS(data);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  function set(key, value) { setS((p) => ({ ...p, [key]: value })); }

  async function save() {
    setSaving(true);
    try {
      const payload = { ...s };
      delete payload.id;
      delete payload.updated_at;
      const { data } = await api.patch("/admin/site", payload);
      setS(data);
      updateCachedSettings(data);
      // Tell AdminLayout to re-apply CSS variables in case the theme changed.
      window.dispatchEvent(new CustomEvent("lumetier:theme-changed"));
      toast.success("Site settings saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  }

  if (loading || !s) {
    return <div className="text-muted-foreground">Loading site settings…</div>;
  }

  return (
    <div className="space-y-10" data-testid="admin-site-page">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="eyebrow mb-2">Site Content</div>
          <h1 className="font-serif text-4xl font-light">Public site settings</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">
            Edit your homepage hero, portfolio gallery, and contact details. Changes save instantly to the live preview site.
          </p>
        </div>
        <button
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 px-5 h-11 bg-primary text-primary-foreground text-xs uppercase tracking-[0.18em] hover:bg-primary/90 disabled:opacity-60"
          data-testid="site-save-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>

      <MigrationPanel />

      {/* THEME */}
      <ThemeSection s={s} set={set} />

      {/* HERO */}
      <Section title="Homepage hero" subtitle="The first thing visitors see when they land on your booking site.">
        <div className="grid md:grid-cols-2 gap-8">
          <HeroImageUploader
            value={s.hero_image}
            onChange={(v) => set("hero_image", v)}
            studioName={s.studio_name}
          />
          <div className="space-y-4">
            <Field label="Eyebrow (small text above headline)">
              <input value={s.hero_eyebrow || ""} onChange={(e) => set("hero_eyebrow", e.target.value)} className="input" data-testid="hero-eyebrow" />
            </Field>
            <Field label="Headline — line 1">
              <input value={s.hero_title_line1 || ""} onChange={(e) => set("hero_title_line1", e.target.value)} className="input" data-testid="hero-title-1" />
            </Field>
            <Field label="Headline — line 2">
              <input value={s.hero_title_line2 || ""} onChange={(e) => set("hero_title_line2", e.target.value)} className="input" data-testid="hero-title-2" />
            </Field>
            <Field label="Description">
              <textarea rows={3} value={s.hero_description || ""} onChange={(e) => set("hero_description", e.target.value)} className="input resize-none" data-testid="hero-desc" />
            </Field>
          </div>
        </div>
      </Section>

      {/* ABOUT */}
      <Section title="About / studio intro" subtitle="The section below the hero.">
        <div className="grid md:grid-cols-2 gap-6">
          <Field label="Eyebrow">
            <input value={s.about_eyebrow || ""} onChange={(e) => set("about_eyebrow", e.target.value)} className="input" data-testid="about-eyebrow" />
          </Field>
          <Field label="Title">
            <input value={s.about_title || ""} onChange={(e) => set("about_title", e.target.value)} className="input" data-testid="about-title" />
          </Field>
          <div className="md:col-span-2">
            <Field label="Body">
              <textarea rows={4} value={s.about_body || ""} onChange={(e) => set("about_body", e.target.value)} className="input resize-none" data-testid="about-body" />
            </Field>
          </div>
        </div>
      </Section>

      {/* PORTFOLIO */}
      <Section title="Portfolio gallery" subtitle="Selected work shown on your public booking site.">
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <Field label="Section eyebrow (small label above the heading)">
            <input
              value={s.portfolio_eyebrow ?? ""}
              onChange={(e) => set("portfolio_eyebrow", e.target.value)}
              className="input"
              placeholder="Selected Work"
              data-testid="portfolio-eyebrow"
            />
          </Field>
          <Field label="Section heading">
            <input
              value={s.portfolio_title ?? ""}
              onChange={(e) => set("portfolio_title", e.target.value)}
              className="input"
              placeholder="Recent sessions"
              data-testid="portfolio-title"
            />
          </Field>
        </div>
        <PortfolioEditor
          images={s.portfolio_images || []}
          onChange={(imgs) => set("portfolio_images", imgs)}
        />
      </Section>

      {/* CONTACT */}
      <Section title="Contact & social" subtitle="Shown on the Contact page and in the footer.">
        <div className="grid md:grid-cols-2 gap-6">
          <Field label="Public email">
            <input value={s.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} className="input" data-testid="contact-email-setting" />
          </Field>
          <Field label="Phone">
            <input value={s.contact_phone || ""} onChange={(e) => set("contact_phone", e.target.value)} className="input" data-testid="contact-phone-setting" />
          </Field>
          <Field label="Location">
            <input value={s.contact_location || ""} onChange={(e) => set("contact_location", e.target.value)} className="input" data-testid="contact-location-setting" />
          </Field>
          <Field label="Instagram URL">
            <input value={s.instagram_url || ""} onChange={(e) => set("instagram_url", e.target.value)} placeholder="https://instagram.com/allureimage" className="input" data-testid="instagram-url" />
          </Field>
          <Field label="Facebook URL">
            <input value={s.facebook_url || ""} onChange={(e) => set("facebook_url", e.target.value)} placeholder="https://facebook.com/allureimage" className="input" data-testid="facebook-url" />
          </Field>
        </div>
      </Section>

      {/* CLIENT LINKS / DOMAIN */}
      <Section
        title="Client links & domain"
        subtitle="The web address used in every link sent to clients — gallery shares, contract signing, review requests and emails."
      >
        <Field label="Public site URL (your custom domain)">
          <input
            value={s.public_site_url || ""}
            onChange={(e) => set("public_site_url", e.target.value)}
            placeholder="https://allure-image.com"
            className="input"
            data-testid="public-site-url-setting"
          />
        </Field>
        <p className="text-xs text-muted-foreground mt-3">
          When set, all client links use this domain — even if you open the dashboard from a different URL.
          Leave blank to build links from whichever address you&rsquo;re using.
        </p>
      </Section>

      {/* PAYMENTS / ZELLE + SENDER */}
      <Section title="Payments — Zelle" subtitle="Shown to clients on booking confirmation emails and on the booking page. Leave blank to fall back to the server default.">
        <div className="grid md:grid-cols-2 gap-6">
          <Field label="Zelle email">
            <input value={s.zelle_email || ""} onChange={(e) => set("zelle_email", e.target.value)} placeholder="payments@allureimage.com" className="input" data-testid="zelle-email-setting" />
          </Field>
          <Field label="Zelle recipient name">
            <input value={s.zelle_name || ""} onChange={(e) => set("zelle_name", e.target.value)} placeholder="Allure Image Photography" className="input" data-testid="zelle-name-setting" />
          </Field>
          <Field label="Studio name (used in email subjects & signatures)">
            <input value={s.studio_name || ""} onChange={(e) => set("studio_name", e.target.value)} placeholder="Allure Image" className="input" data-testid="studio-name-setting" />
          </Field>
        </div>
        <p className="text-xs text-muted-foreground mt-4">Changes go live immediately after you click <strong>Save changes</strong>. No redeploy needed.</p>
      </Section>

      <EmailSenderSection s={s} set={set} />

      <EmailTestPanel />

      {/* POLICY */}
      <Section title="Booking policy & studio rules" subtitle="Customers must read and accept this before booking. Plain text — use blank lines for paragraph breaks.">
        <textarea
          rows={16}
          value={s.booking_policy || ""}
          onChange={(e) => set("booking_policy", e.target.value)}
          className="input resize-y font-mono text-sm leading-relaxed"
          data-testid="booking-policy"
          placeholder="1. DEPOSIT — A non-refundable deposit is required to secure your session date..."
        />
      </Section>

      <div className="flex justify-end">
        <button
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 px-6 h-11 bg-primary text-primary-foreground text-xs uppercase tracking-[0.18em] hover:bg-primary/90 disabled:opacity-60"
          data-testid="site-save-btn-bottom"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>

      <style>{`
        .input { margin-top: .25rem; width: 100%; background: transparent; border-bottom: 1px solid hsl(var(--border)); padding: .6rem 0; outline: none; font-size: .9rem; }
        .input:focus { border-color: hsl(var(--foreground)); }
      `}</style>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <section className="bg-card border border-border p-7">
      <div className="mb-6">
        <h2 className="font-serif text-2xl">{title}</h2>
        {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
      </div>
      {children}
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="eyebrow">{label}</span>
      {children}
    </label>
  );
}

function HeroImageUploader({ value, onChange, studioName }) {
  /* Polished hero-image uploader designed to match the EXACT public-page rendering
     so what you see here is what /s/{slug} will show. Three core states:
       1. Empty — big drop-zone with file picker, drag-drop, and a URL-paste popover.
       2. Set   — live 4:5 preview (matches StudioPublic.jsx hero ratio) with
                  hover controls (Replace · URL · Remove) and a meta strip showing
                  file size + dimensions for freshly uploaded files.
       3. Uploading — spinner overlay with progress messaging.

     The recommended dimensions (1600×2000, 4:5) and 8 MB cap are surfaced inline
     so photographers know what to upload. */
  const inputRef = useRef(null);
  const replaceRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [showUrl, setShowUrl] = useState(false);
  const [pasteUrl, setPasteUrl] = useState("");
  const [meta, setMeta] = useState(null); // {size_kb, width, height}

  const hasImage = !!(value && value !== "/hero.jpg");

  async function uploadFile(file) {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      return toast.error("Please choose an image file (.jpg, .png, .webp)");
    }
    if (file.size > 15 * 1024 * 1024) {
      return toast.error("Hero image must be under 15 MB");
    }
    setUploading(true);
    try {
      // Read width/height locally before upload so we can show meta immediately
      // and warn if the photo isn't the recommended portrait orientation.
      const dims = await readImageDimensions(file);
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      setMeta({
        size_kb: Math.round(file.size / 102.4) / 10,
        width: dims.width,
        height: dims.height,
      });
      if (dims.width > dims.height) {
        toast.warning("This image is landscape — the hero crops to portrait (4:5). Consider a taller crop.");
      } else {
        toast.success("Hero image uploaded");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
      if (replaceRef.current) replaceRef.current.value = "";
    }
  }

  function applyUrl() {
    const url = pasteUrl.trim();
    if (!url) return;
    onChange(url);
    setMeta(null);
    setShowUrl(false);
    setPasteUrl("");
    toast.success("Hero image URL applied");
  }

  function clearImage() {
    if (!window.confirm("Remove the hero image? The public page will fall back to the placeholder.")) return;
    onChange("");
    setMeta(null);
    toast("Hero cleared");
  }

  function onDrop(e) {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) uploadFile(file);
  }

  return (
    <div className="space-y-3" data-testid="hero-image-uploader">
      <div className="flex items-baseline justify-between gap-2">
        <span className="eyebrow">Hero image</span>
        <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
          Portrait · 4:5 · 1600×2000 recommended
        </span>
      </div>

      {hasImage ? (
        /* ----- SET STATE ----- */
        <div className="group relative aspect-[4/5] bg-muted overflow-hidden border border-border" data-testid="hero-image-preview">
          <img src={value} alt="Hero preview" className="absolute inset-0 w-full h-full object-cover" />
          {uploading && (
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center" data-testid="hero-uploading">
              <Loader2 className="w-6 h-6 text-white animate-spin" />
            </div>
          )}
          {/* Hover toolbar */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/0 via-black/0 to-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-between p-3">
            <div className="text-xs text-white/90 max-w-[60%] truncate" title={value}>
              {value.startsWith("http") ? new URL(value, window.location.origin).pathname : value}
            </div>
            <div className="flex items-center gap-1">
              <input
                ref={replaceRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => uploadFile(e.target.files?.[0])}
                data-testid="hero-replace-input"
              />
              <button
                type="button"
                onClick={() => replaceRef.current?.click()}
                title="Replace image"
                data-testid="hero-replace-btn"
                className="w-8 h-8 flex items-center justify-center bg-white/90 text-stone-900 hover:bg-white"
              >
                <RefreshCw className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={() => setShowUrl((v) => !v)}
                title="Paste URL"
                data-testid="hero-url-toggle"
                className="w-8 h-8 flex items-center justify-center bg-white/90 text-stone-900 hover:bg-white"
              >
                <LinkIcon className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={clearImage}
                title="Remove image"
                data-testid="hero-remove-btn"
                className="w-8 h-8 flex items-center justify-center bg-rose-600/90 text-white hover:bg-rose-700"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
          {/* Crop overlay on hover so photographers see how the 4:5 frame will cover landscape uploads */}
          <div className="absolute top-2 left-2 px-2 py-1 bg-black/60 text-white text-[10px] uppercase tracking-[0.18em] opacity-0 group-hover:opacity-100 transition-opacity">
            Live preview · matches /{studioName ? studioName.toLowerCase().replace(/\s+/g, "-") : "your-slug"}
          </div>
        </div>
      ) : (
        /* ----- EMPTY STATE ----- */
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          data-testid="hero-dropzone"
          className={`group block w-full aspect-[4/5] border-2 border-dashed transition-colors ${dragging ? "border-foreground bg-muted/40" : "border-border hover:border-foreground/60 bg-muted/20"} flex flex-col items-center justify-center gap-3 text-center px-6`}
        >
          {uploading ? (
            <>
              <Loader2 className="w-8 h-8 animate-spin text-foreground" />
              <div className="text-sm">Uploading your hero image…</div>
            </>
          ) : (
            <>
              <div className="w-14 h-14 border border-border flex items-center justify-center group-hover:border-foreground/60 transition-colors">
                <ImagePlus className="w-6 h-6 text-muted-foreground group-hover:text-foreground" />
              </div>
              <div>
                <div className="font-medium">Add your hero image</div>
                <div className="text-xs text-muted-foreground mt-1">
                  Drop a photo here or click to browse
                </div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mt-3">
                  PNG, JPG, or WebP · up to 15 MB
                </div>
              </div>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setShowUrl(true); }}
                className="text-xs text-muted-foreground underline hover:text-foreground mt-2"
                data-testid="hero-paste-url-link"
              >
                or paste an image URL
              </button>
            </>
          )}
        </button>
      )}

      {/* Hidden file input — used by both empty-state click and the Replace button */}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => uploadFile(e.target.files?.[0])}
        data-testid="hero-file-input"
      />

      {/* URL paste row — toggled from empty state and hover toolbar */}
      {showUrl && (
        <div className="flex items-center gap-2" data-testid="hero-url-row">
          <input
            value={pasteUrl}
            onChange={(e) => setPasteUrl(e.target.value)}
            placeholder="https://example.com/hero.jpg"
            className="flex-1 bg-transparent border-b border-border py-2 outline-none focus:border-foreground text-sm"
            data-testid="hero-url-input"
          />
          <button
            type="button"
            onClick={applyUrl}
            className="px-3 h-9 text-xs uppercase tracking-[0.18em] bg-foreground text-background"
            data-testid="hero-url-apply"
          >
            Apply
          </button>
          <button
            type="button"
            onClick={() => { setShowUrl(false); setPasteUrl(""); }}
            className="w-9 h-9 flex items-center justify-center border border-border"
            aria-label="Cancel"
            data-testid="hero-url-cancel"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Meta strip — only shown for fresh uploads where we know dimensions */}
      {meta && (
        <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.22em] text-muted-foreground border-t border-border pt-2" data-testid="hero-meta">
          <span>{meta.width} × {meta.height}px</span>
          <span>{meta.size_kb < 1024 ? `${meta.size_kb} KB` : `${(meta.size_kb / 1024).toFixed(1)} MB`}</span>
          {meta.width > meta.height && (
            <span className="text-amber-600">Landscape · will crop</span>
          )}
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        Tip: a vertical (portrait) photo of your subject or workspace works best — the hero crops to 4:5.
      </p>
    </div>
  );
}

/* Read width/height from a File so we can warn about wrong-aspect uploads BEFORE
   the server takes the bytes. Avoids a server round-trip just to read dimensions. */
function readImageDimensions(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(url);
    };
    img.onerror = () => {
      resolve({ width: 0, height: 0 });
      URL.revokeObjectURL(url);
    };
    img.src = url;
  });
}

function ImageUploader({ value, onChange, testid }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image");
    if (file.size > 15 * 1024 * 1024) return toast.error("Image larger than 15 MB");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  return (
    <div className="flex items-center gap-2">
      <input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Paste URL or upload →"
        className="flex-1 bg-transparent border-b border-border py-2 outline-none focus:border-foreground text-sm"
        data-testid={`${testid}-url`}
      />
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid={`${testid}-file`} />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="inline-flex items-center gap-2 px-3 h-9 text-xs uppercase tracking-[0.18em] border border-border hover:border-foreground/60 disabled:opacity-60"
        data-testid={`${testid}-upload-btn`}
      >
        {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
        {uploading ? "Uploading…" : "Upload"}
      </button>
    </div>
  );
}

function PortfolioEditor({ images, onChange }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [pasteUrl, setPasteUrl] = useState("");

  async function uploadFiles(files) {
    setUploading(true);
    try {
      const next = [...images];
      for (const file of files) {
        if (!file.type.startsWith("image/")) continue;
        if (file.size > 15 * 1024 * 1024) {
          toast.error(`${file.name} is larger than 15 MB — skipped`);
          continue;
        }
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        next.push(data.url);
      }
      onChange(next);
      toast.success(`${files.length} image${files.length > 1 ? "s" : ""} added`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  function addUrl() {
    if (!pasteUrl.trim()) return;
    onChange([...images, pasteUrl.trim()]);
    setPasteUrl("");
  }

  function remove(idx) {
    onChange(images.filter((_, i) => i !== idx));
  }

  function move(idx, dir) {
    const next = [...images];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => uploadFiles(Array.from(e.target.files || []))}
          data-testid="portfolio-file-input"
        />
        <button
          type="button"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          className="inline-flex items-center gap-2 px-4 h-10 bg-foreground text-background text-xs uppercase tracking-[0.18em] hover:bg-foreground/90 disabled:opacity-60"
          data-testid="portfolio-upload-btn"
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {uploading ? "Uploading…" : "Upload photos"}
        </button>

        <div className="flex flex-1 gap-2">
          <input
            value={pasteUrl}
            onChange={(e) => setPasteUrl(e.target.value)}
            placeholder="…or paste an image URL"
            className="flex-1 bg-transparent border-b border-border py-2 outline-none focus:border-foreground text-sm"
            data-testid="portfolio-paste-url"
          />
          <button
            type="button"
            onClick={addUrl}
            className="inline-flex items-center gap-1 px-3 h-10 text-xs uppercase tracking-[0.18em] border border-border hover:border-foreground/60"
            data-testid="portfolio-add-url-btn"
          >
            <Plus className="w-3.5 h-3.5" /> Add
          </button>
        </div>
      </div>

      {images.length === 0 ? (
        <div className="border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
          No photos yet. Upload images or paste URLs to start your gallery.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="portfolio-list">
          {images.map((src, i) => (
            <div key={`${src}-${i}`} className="group relative aspect-[4/5] bg-muted overflow-hidden border border-border">
              <img src={src} alt={`portfolio ${i + 1}`} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
                <IconBtn label="↑" onClick={() => move(i, -1)} testid={`portfolio-up-${i}`} />
                <IconBtn label="↓" onClick={() => move(i, 1)} testid={`portfolio-down-${i}`} />
                <IconBtn label={<X className="w-3.5 h-3.5" />} onClick={() => remove(i)} testid={`portfolio-remove-${i}`} destructive />
              </div>
              <div className="absolute top-1.5 left-1.5 text-[10px] bg-background/80 px-1.5 py-0.5">{i + 1}</div>
            </div>
          ))}
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        Photos appear in the order shown. Hover any photo to reorder or remove. Don't forget to click <strong>Save changes</strong>.
      </p>
    </div>
  );
}

function IconBtn({ label, onClick, testid, destructive }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`w-8 h-8 flex items-center justify-center text-xs ${destructive ? "bg-destructive text-destructive-foreground" : "bg-background text-foreground"}`}
    >
      {label}
    </button>
  );
}

function EmailSenderSection({ s, set }) {
  const sender = (s.sender_email || "").trim();
  const onSandbox = !sender || sender.toLowerCase().includes("resend.dev");

  return (
    <section className="bg-card border border-border p-7" data-testid="email-sender-section">
      <div className="mb-5">
        <div className="eyebrow mb-2">Outgoing email</div>
        <h2 className="font-serif text-2xl">Sender address</h2>
        <p className="text-sm text-muted-foreground mt-1">
          The "From" address on every email your studio sends. Must be on a domain you've verified in Resend.
        </p>
      </div>

      {onSandbox && (
        <div className="mb-5 flex items-start gap-3 border border-amber-500/50 bg-amber-50 text-amber-900 px-4 py-3 text-sm" data-testid="sender-sandbox-warning">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <strong>Customer emails are restricted.</strong> While the sender address is empty or on Resend's sandbox domain
            (<code className="bg-amber-100 px-1">onboarding@resend.dev</code>), Resend will only deliver to your own
            account email. To send to actual clients, set the sender to an address on your verified domain
            (e.g. <code className="bg-amber-100 px-1">bookings@allure-image.com</code>) and click <strong>Save changes</strong>.
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        <Field label="Sender email (must be on a domain verified in Resend)">
          <input
            value={s.sender_email || ""}
            onChange={(e) => set("sender_email", e.target.value)}
            placeholder="bookings@allure-image.com"
            className="input"
            data-testid="sender-email-setting"
          />
        </Field>
        <Field label="Sender display name (optional)">
          <input
            value={s.sender_name || ""}
            onChange={(e) => set("sender_name", e.target.value)}
            placeholder="Allure Image"
            className="input"
            data-testid="sender-name-setting"
          />
        </Field>
      </div>

      <p className="text-xs text-muted-foreground mt-4">
        Changes go live the moment you click <strong>Save changes</strong>. No redeploy needed.
        Leave blank to fall back to the server's <code>SENDER_EMAIL</code> environment variable.
      </p>
    </section>
  );
}

function EmailTestPanel() {
  const [testTo, setTestTo] = useState("");
  const [sending, setSending] = useState(false);

  async function sendTest() {
    if (!testTo || !testTo.includes("@")) return toast.error("Enter a valid email to send to");
    setSending(true);
    try {
      await api.post("/admin/config/test-email", { to: testTo });
      toast.success(`Test email sent to ${testTo}. Check inbox (and spam).`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Test email failed");
    } finally {
      setSending(false);
    }
  }

  return (
    <section className="bg-card border border-border p-7" data-testid="email-test-section">
      <div className="mb-5">
        <h2 className="font-serif text-2xl">Send a test email</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Sends a tiny email so you can confirm delivery is working. Sender details come from your server configuration.
        </p>
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          value={testTo}
          onChange={(e) => setTestTo(e.target.value)}
          placeholder="you@example.com"
          className="flex-1 bg-transparent border-b border-border py-2 outline-none focus:border-foreground text-sm"
          data-testid="test-email-to"
        />
        <button
          onClick={sendTest}
          disabled={sending}
          className="inline-flex items-center gap-2 px-4 h-10 border border-border hover:border-foreground/60 text-xs uppercase tracking-[0.18em] disabled:opacity-60"
          data-testid="send-test-email-btn"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {sending ? "Sending…" : "Send test"}
        </button>
      </div>
    </section>
  );
}

function MigrationPanel() {
  const fileRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [lastImport, setLastImport] = useState(null);

  async function handleExport() {
    setExporting(true);
    try {
      const { data } = await api.get("/admin/migration/site-export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `allure-site-export-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      const fileCount = Object.keys(data.files || {}).length;
      toast.success(`Exported ${data.services?.length || 0} services + ${fileCount} images`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Export failed");
    } finally {
      setExporting(false);
    }
  }

  async function handleImport(file) {
    if (!file) return;
    const ok = window.confirm(
      "Importing will OVERWRITE this environment's services and site settings with the contents of the JSON file. Continue?"
    );
    if (!ok) return;
    setImporting(true);
    try {
      const text = await file.text();
      const payload = JSON.parse(text);
      const { data } = await api.post("/admin/migration/site-import", payload);
      setLastImport(data);
      toast.success(`Imported ${data.services_written} services + ${data.files_imported} images`);
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Import failed — invalid file?");
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  return (
    <section className="bg-card border border-border p-6" data-testid="migration-panel">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div className="max-w-2xl">
          <div className="eyebrow mb-2">Site migration</div>
          <h2 className="font-serif text-2xl mb-2">Move your content between preview & production</h2>
          <p className="text-sm text-muted-foreground">
            Each environment has its own database. Use <strong>Export</strong> here to download a JSON bundle of all
            your services, portfolio, and hero images. Then sign in to the other environment's Studio Manager and use
            <strong> Import</strong> to apply the same content there. Import overwrites the existing services and site settings.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleExport}
            disabled={exporting}
            className="inline-flex items-center gap-2 px-4 h-10 border border-border hover:border-foreground/60 text-xs uppercase tracking-[0.18em] disabled:opacity-60"
            data-testid="migration-export-btn"
          >
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            {exporting ? "Exporting…" : "Export site data"}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={(e) => handleImport(e.target.files?.[0])}
            data-testid="migration-import-input"
          />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={importing}
            className="inline-flex items-center gap-2 px-4 h-10 bg-foreground text-background text-xs uppercase tracking-[0.18em] hover:bg-foreground/90 disabled:opacity-60"
            data-testid="migration-import-btn"
          >
            {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileUp className="w-4 h-4" />}
            {importing ? "Importing…" : "Import site data"}
          </button>
        </div>
      </div>
      {lastImport && (
        <div className="mt-4 text-xs text-muted-foreground border-t border-border pt-3" data-testid="migration-last-result">
          Last import: {lastImport.services_written} services · {lastImport.files_imported} images · site_settings {lastImport.site_settings_written ? "applied" : "skipped"}. Reloading…
        </div>
      )}
    </section>
  );
}



/* ------------------------------------------------------------------------
   Theme picker — Lumetier per-studio personalization.

   Lets the studio owner pick from 8 curated presets and (optionally) override
   individual colors with hex inputs. Live preview by writing CSS vars onto a
   scoped wrapper element AND a small mock card. Live admin-chrome preview is
   triggered by also pushing the in-progress palette onto documentElement, then
   rolling back when the user navigates away or clicks "Reset".
   ------------------------------------------------------------------------ */
function ThemeSection({ s, set }) {
  const currentThemeId = s.theme_id || DEFAULT_THEME_ID;
  const currentOverrides = s.theme_overrides || {};
  const resolved = resolvePalette(currentThemeId, currentOverrides);
  const hasOverrides = Object.keys(currentOverrides).filter((k) => currentOverrides[k]).length > 0;

  // Live-preview the in-progress palette on the documentElement so studio
  // owners can SEE the Studio Manager chrome shift while they're picking. Rolls
  // back when the panel unmounts (route change). The PATCH /admin/site save
  // still persists the final value separately.
  const overridesKey = JSON.stringify(currentOverrides);
  useEffect(() => {
    const cleanup = applyTheme({ themeId: currentThemeId, overrides: JSON.parse(overridesKey) });
    return cleanup;
  }, [currentThemeId, overridesKey]);

  function pickTheme(id) {
    set("theme_id", id);
    // When switching presets, clear in-flight overrides so the new preset shows
    // in its pure form. Owner can re-customize after picking.
    set("theme_overrides", {});
  }

  function setOverride(key, hex) {
    const next = { ...currentOverrides };
    if (!hex || hex === getThemeById(currentThemeId).palette[key]) {
      delete next[key];
    } else {
      next[key] = hex;
    }
    set("theme_overrides", next);
  }

  function resetOverrides() {
    set("theme_overrides", {});
    toast("Reverted to preset colors");
  }

  return (
    <section className="bg-card border border-border p-7" data-testid="theme-section">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Palette className="w-4 h-4 text-muted-foreground" />
            <h2 className="font-serif text-2xl">Theme & colors</h2>
          </div>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Pick a preset that fits your brand, then fine-tune any color. Changes preview live across this
            Studio Manager AND your public booking site.
          </p>
        </div>
        {hasOverrides && (
          <button
            type="button"
            onClick={resetOverrides}
            className="inline-flex items-center gap-2 px-3 h-9 border border-border hover:border-foreground/60 text-xs uppercase tracking-[0.18em] shrink-0"
            data-testid="theme-reset-overrides"
            title="Clear color overrides and use the preset's defaults"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Reset overrides
          </button>
        )}
      </div>

      {/* Preset grid — 2 columns on mobile, 3 on md, all on lg */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 mb-8" data-testid="theme-preset-grid">
        {THEMES.map((t) => {
          const active = t.id === currentThemeId;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => pickTheme(t.id)}
              data-testid={`theme-card-${t.id}`}
              className={`group text-left border-2 transition-all overflow-hidden hover:shadow-md ${
                active ? "border-foreground ring-1 ring-foreground/20" : "border-border hover:border-foreground/40"
              }`}
            >
              {/* 5-color swatch strip */}
              <div className="flex h-14">
                <div className="flex-1" style={{ background: t.palette.background }} />
                <div className="flex-[1.5]" style={{ background: t.palette.primary }} />
                <div className="flex-1" style={{ background: t.palette.accent }} />
                <div className="flex-1" style={{ background: t.palette.muted }} />
                <div className="flex-1" style={{ background: t.palette.foreground }} />
              </div>
              {/* Mini typography preview */}
              <div
                className="px-3 pt-2 pb-1"
                style={{ background: t.palette.background, color: t.palette.foreground }}
              >
                <div
                  className="text-[9px] uppercase tracking-[0.22em] font-sans"
                  style={{ color: t.palette.accent }}
                >
                  Studio
                </div>
                <div className="font-serif text-sm leading-tight mt-0.5">
                  Allure Image
                </div>
              </div>
              {/* Label row */}
              <div className="px-3 pt-1 pb-3 bg-card relative">
                {active && (
                  <span
                    className="absolute top-2 right-2 inline-flex items-center justify-center w-5 h-5 bg-foreground text-background"
                    data-testid={`theme-active-badge-${t.id}`}
                  >
                    <Check className="w-3 h-3" />
                  </span>
                )}
                <div className="text-sm font-medium pr-6 leading-snug">{t.name}</div>
                <div className="text-[10px] text-muted-foreground mt-0.5 line-clamp-2 leading-relaxed">
                  {t.description}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Live mock + per-color editor */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* MOCK PREVIEW — uses the resolved palette as inline CSS so it ignores
            documentElement and always shows the final result for the currently-
            edited theme, including unsaved overrides. */}
        <div
          className="border border-border overflow-hidden"
          style={{ background: resolved.background, color: resolved.foreground }}
          data-testid="theme-mock-preview"
        >
          <div
            className="px-5 py-4 border-b"
            style={{ borderColor: shiftHexLightness(resolved.background, -8) }}
          >
            <div className="text-[10px] uppercase tracking-[0.28em]" style={{ color: resolved.accent }}>
              Live preview
            </div>
            <div className="font-serif text-2xl mt-1" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
              {s.studio_name || "Your studio"}
            </div>
          </div>
          <div className="p-5 space-y-4">
            <p className="text-sm" style={{ opacity: 0.75 }}>
              This is how a paragraph of body copy will read on your public site. Pick colors that feel like your brand.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                className="px-4 h-10 text-xs uppercase tracking-[0.22em]"
                style={{ background: resolved.primary, color: resolved.primary_foreground }}
                data-testid="theme-preview-primary-btn"
              >
                Book a session
              </button>
              <button
                type="button"
                className="px-4 h-10 text-xs uppercase tracking-[0.22em] border"
                style={{ borderColor: shiftHexLightness(resolved.background, -15), color: resolved.foreground }}
              >
                Learn more
              </button>
            </div>
            <div
              className="px-4 py-3 text-xs"
              style={{ background: resolved.muted, color: resolved.foreground }}
            >
              Tip: muted surfaces work for the about section, contact form, and footer.
            </div>
          </div>
        </div>

        {/* PER-COLOR EDITOR */}
        <div className="space-y-3" data-testid="theme-overrides-editor">
          <div className="eyebrow mb-1">Customize colors</div>
          <p className="text-xs text-muted-foreground mb-2">
            Each input shows the preset&apos;s color by default. Change one and we&apos;ll override just that key — leaving the
            others on the preset.
          </p>
          {THEME_KEYS.map(({ key, label }) => {
            const presetColor = getThemeById(currentThemeId).palette[key];
            const overrideColor = currentOverrides[key];
            const effective = overrideColor || presetColor;
            return (
              <div key={key} className="flex items-center gap-3" data-testid={`theme-override-row-${key}`}>
                <div className="w-9 h-9 border border-border shrink-0" style={{ background: effective }} />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium">{label}</div>
                  <div className="text-[10px] text-muted-foreground">
                    {overrideColor ? "Override active" : `Preset: ${presetColor}`}
                  </div>
                </div>
                <input
                  type="color"
                  value={effective}
                  onChange={(e) => setOverride(key, e.target.value)}
                  className="w-9 h-9 border border-border cursor-pointer p-0 shrink-0"
                  aria-label={`${label} color picker`}
                  data-testid={`theme-override-color-${key}`}
                />
                <input
                  type="text"
                  value={effective}
                  onChange={(e) => setOverride(key, e.target.value)}
                  placeholder={presetColor}
                  className="w-24 text-xs font-mono border border-border bg-background px-2 py-1.5 focus:outline-none focus:border-foreground shrink-0"
                  data-testid={`theme-override-hex-${key}`}
                />
                {overrideColor ? (
                  <button
                    type="button"
                    onClick={() => setOverride(key, "")}
                    className="text-muted-foreground hover:text-foreground shrink-0"
                    title="Clear override"
                    data-testid={`theme-override-clear-${key}`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                ) : (
                  <span className="w-3.5 shrink-0" />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* Tiny hex-lightness shifter used only for the inline mock preview. Mirrors the
   helper in lib/themeApplier.js but returns a hex (since the mock paints with
   raw hex via inline style). */
function shiftHexLightness(hex, delta) {
  if (!hex || typeof hex !== "string") return hex;
  let h = hex.replace(/^#/, "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (!/^[0-9a-f]{6}$/i.test(h)) return hex;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const adjust = (c) => Math.min(255, Math.max(0, c + Math.round((delta / 100) * 255)));
  const next = [adjust(r), adjust(g), adjust(b)]
    .map((c) => c.toString(16).padStart(2, "0"))
    .join("");
  return `#${next}`;
}
